import React, { useEffect, useState } from "react";
import { Loader2, Mail, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Single unified invite dialog used by Admin → Users AND Settings → Companies.
 *
 * Calls `POST /api/admin/users/invite` which:
 *   - Adds an active membership if the email is already registered.
 *   - Creates a pending membership otherwise (auto-accepted on first register/login).
 *   - When `send_email` is true → emails a magic sign-in link (existing) or
 *     a pre-filled register link (new) via Resend.
 */
export default function InviteUserDialog({ open, onClose, companyName, onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("employee");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setRole("employee");
      setSendEmail(true);
      setBusy(false);
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/invite", {
        email: clean,
        role,
        send_email: sendEmail,
      });
      const bits = [];
      bits.push(data.auto_added ? "Added to workspace" : "Invite pending registration");
      if (data.email_sent) bits.push("email sent");
      toast.success(bits.join(" · "));
      onInvited?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-[440px]" data-testid="invite-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-600" /> Invite to {companyName || "this workspace"}
          </DialogTitle>
          <DialogDescription>
            They'll be added on registration (or immediately if they already have an account).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-1">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Email</div>
            <input
              autoFocus
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@example.com"
              className="tg-input w-full"
              data-testid="invite-email-input"
            />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Role</div>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="tg-input w-full"
              data-testid="invite-role-select"
            >
              {["employee", "supervisor", "advisor", "founder", "admin"].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Pick <span className="font-mono">admin</span> to give them full workspace control.
            </p>
          </div>
          <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="invite-send-email-toggle">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-emerald-600"
            />
            <span className="text-sm text-slate-700">
              <span className="font-medium flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Send sign-in email now</span>
              <span className="block text-xs text-slate-500">Magic link for existing users · register link for new ones.</span>
            </span>
          </label>
          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="tg-btn-secondary"
              data-testid="invite-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="tg-btn-primary disabled:opacity-50"
              data-testid="invite-submit-btn"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {sendEmail ? "Invite & send" : "Invite"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
